<template>
    <div class="line-departure-set" :class="isMPC ? 'pa-0 mt-2 mb-8 container' : 'v-col v-col-3'">
        <v-row v-if="isMPC" no-gutters justify="center">
            <v-col cols="12" sm="8" data-id="departures-list-mpc">
                <v-row no-gutters justify="center" align="center">
                    <v-col cols="12" sm="8">
                        <h4 class="regular text-center">Til {{ name }}</h4>
                        <h5 class="body-2 text-center departures-subtitle">Fra {{ location }}</h5>
                    </v-col>
                    <v-col v-if="isMPC" cols="12" sm="4" class="pt-2 pt-sm-0">
                        <div class="subtitle-2 text-center">Antall reisende</div>
                        <v-row no-gutters justify="center" align="center">
                            <v-col v-if="showTotalPassengers" cols="auto" class="text-center" style="min-width: 50px">
                                <total-riders :value="totalPassengers" icon="mdi-human-male" />
                            </v-col>
                            <v-col v-if="showTotalCars" cols="auto" class="text-center" style="min-width: 50px">
                                <total-riders :value="totalCars" icon="mdi-car" />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-btn
                    v-for="departure in filteredItems"
                    :id="departure.tripId"
                    :key="departure.tripId"
                    :to="{
                        name: 'trip',
                        params: {
                            lineNumber: lineNumber,
                            tripId: departure.tripId,
                            stopIndex: 0
                        },
                        query: {
                            token: router.currentRoute.value.query.token,
                            date: $route.query.date,
                            view: selectedView
                        }
                    }"
                    large
                    block
                    min-height="44"
                    height="auto"
                    class="my-2 list-btn"
                    :class="{
                        'font-weight-black': departure.serviceJourneyForDay.isOnDuty
                    }"
                    :line-private-code="lineNumber"
                >
                    <v-row no-gutters justify="center" align="center">
                        <v-col class="text-right text-wrap">
                            {{ departure.vesselName }}
                        </v-col>
                        <v-col>
                            <v-row no-gutters justify="center">
                                <v-col cols="auto" class="px-8">
                                    {{ time(departure.serviceJourneyForDay.originDeparture) }}
                                </v-col>
                                <v-col v-if="isMPC" cols="auto">
                                    <v-row no-gutters justify="center">
                                        <v-col v-if="showTotalPassengers" cols="auto" class="text-center" style="min-width: 50px">
                                            <total-riders :value="departure.totalPassengers" icon="mdi-human-male" />
                                        </v-col>
                                        <v-col v-if="showTotalCars" cols="auto" class="text-center" style="min-width: 50px">
                                            <total-riders :value="departure.totalCars" icon="mdi-car" />
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col>
                            <div v-if="departure.hasStarted" class="text-center">
                                <span data-id="started">Startet</span>
                            </div>
                            <div v-if="departure.bookedStopCount > 0" class="text-center">
                                <v-icon color="green-accent-2" size="22" icon="mdi-check-circle-outline" class="booking-icon"></v-icon>
                                <span class="booking-text">{{ departure.bookedStopCount }} anløp</span>
                            </div>
                        </v-col>
                    </v-row>
                </v-btn>
                <v-btn
                    v-show="shouldLimit"
                    large
                    block
                    color="white"
                    height="40"
                    data-id="toggle-departures-limit"
                    class="mt-2 text-none font-weight-normal"
                    @click="onToggleLimit"
                >
                    Vis {{ showAll ? 'færre' : 'flere' }} avganger
                </v-btn>
            </v-col>
        </v-row>
        <div v-else>
            <div class="list-title">
                <h4 class="regular text-center">
                    {{ name }}
                </h4>
                <h5 class="body-2 text-center">Fra {{ location }}</h5>
            </div>
            <v-list data-id="departures-list-avvik">
                <v-list-item
                    v-for="departure in filteredItems"
                    :id="departure.tripId"
                    :key="departure.tripId"
                    :to="{
                        name: 'messages',
                        params: {
                            lineNumber: lineNumber,
                            tripId: departure.tripId
                        },
                        query: {
                            token: router.currentRoute.value.query.token,
                            date: $route.query.date,
                            view: selectedView
                        }
                    }"
                    block
                    :class="{
                        'font-weight-black': departure.serviceJourneyForDay.isOnDuty
                    }"
                    :line-private-code="lineNumber"
                >
                    {{ time(departure.serviceJourneyForDay.originDeparture) }}
                </v-list-item>
            </v-list>
            <v-btn
                v-show="shouldLimit"
                large
                block
                color="white"
                height="40"
                data-id="toggle-departures-limit"
                class="text-none font-weight-normal"
                @click="onToggleLimit"
            >
                Vis {{ showAll ? 'færre' : 'flere' }} avganger
            </v-btn>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import TotalRiders from '@/features/departures/TotalRiders.vue';
import { useRouter } from 'vue-router';
import { Views, ViewIndex } from '@/helpers/ViewSwitcher';
import { ServiceJourney } from './ServiceJourney';
import { useDateTimeFilters } from '@/helpers/useDateTimeFilters';

const props = defineProps<{
    name: string;
    location: string;
    items: ServiceJourney[]; // ServiceJourney
    lineNumber: string;
    singleView: boolean;
    isToday: boolean;
    selectedView: string;
}>();

const { time } = useDateTimeFilters();
const showAll = ref(false);
const displayLimit = 5;
const router = useRouter();

function onToggleLimit() {
    showAll.value = !showAll.value;
}

const isMPC = computed(() => {
    return props.selectedView === Views[ViewIndex.mpc];
});

const shouldLimit = computed(() => {
    return props.items.length > displayLimit;
});

const maxCount = computed(() => {
    return Math.min(props.items.length, displayLimit);
});

const filteredItems = computed(() => {
    if (showAll.value || !shouldLimit.value) {
        return props.items;
    } else if (props.isToday) {
        // get first item where isOnDuty is true
        let index = props.items.findIndex(x => x.serviceJourneyForDay.isOnDuty);

        // if no isOnDuty item is found, fetch the one closest to current time
        if (index === -1) {
            const now = new Date().getTime();

            // create a list of time difference
            const timeDiffs = props.items.map(x => Math.abs(x.serviceJourneyForDay.originDeparture.getTime() - now));

            // the index should be the same as in the original list
            index = timeDiffs.indexOf(Math.min(...timeDiffs));
        }

        const itemLimitDiff = props.items.length - displayLimit;
        const lastLimitIndex = itemLimitDiff > 0 ? itemLimitDiff : 0;

        if (index > lastLimitIndex) {
            index = lastLimitIndex;
        }

        return props.items.slice(index !== -1 ? index : 0, (index !== -1 ? index : 0) + maxCount.value);
    } else {
        return props.items.slice(0, maxCount.value);
    }
});

const showTotalPassengers = computed(() => {
    return props.items && props.items.some(x => x.riderTypes.indexOf('passengers') !== -1);
});

const showTotalCars = computed(() => {
    return props.items && props.items.some(x => x.riderTypes.indexOf('cars') !== -1);
});

const totalPassengers = computed(() => {
    return props.items.reduce((acc, curr) => acc + curr.totalPassengers, 0);
});

const totalCars = computed(() => {
    return props.items.reduce((acc, curr) => acc + curr.totalCars, 0);
});
</script>

<style lang="scss">
.v-btn__content {
    flex: 1 1 auto;
}

.departures-subtitle {
    font-weight: normal;
}

.v-list-item {
    justify-content: center;
    background-color: #f5f5f5;
    border-radius: 4px;
    margin: 8px 0;
}

.list-title {
    min-height: 70px;
}

.list-btn {
    background-color: #f5f5f5;
}

.booking-text {
    font-size: 12px;
    margin-left: 5px;
    text-transform: none;
}

.booking-icon {
    padding-bottom: 1px;
}
</style>
